import React, { useState } from 'react'
import './index.scss'
import { NavigateFunction } from 'react-router'
import clsx from 'clsx'
import TagComponent from '@Components/Tag'
import getCatalogPath from '@App/helpers/work-entities/utils/get-catalog-path'
import ExtendInfoToWorkItem from '@Components/ExtendInfoToWorkItem'
import { RightsholderClaimedShare } from '@App/entities/rightsholder'
import { WorkModel } from '@Entities/work/work'
import { getSourceNameBySourceId } from '@App/helpers/work-entities/utils/get-source-name-by-source-id'
import WorkItemRecordings from '@Components/WorkItem/WorkItemRecordings'
import WorkItemTitleRow from '@Components/WorkItem/WorkItemTitleRow'
import WorkItemSharesInfo from '@Components/WorkItem/WorkItemSharesInfo'
import WorkItemDatesInfo from '@Components/WorkItem/WorkItemDatesInfo'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'

interface WorkItemProps {
    navigate?: NavigateFunction
    data: WorkModel
    rightsHolderId?: number
    publicSearchType?: boolean
    catalogType?: boolean
    historyType?: boolean
    draftType?: boolean
    claimType?: boolean
    claimedShare?: RightsholderClaimedShare
    handleDeleteDraft?: () => void
    selectWorkForMatching?: (songCode: string, title: string) => void
    selectWorkForClaiming?: (propertyId: number | null | undefined) => void
    onCatalogClick?: () => void
}

/**
 * @deprecated Use WorkItemV2 instead
 */
const WorkItem = ({
    navigate,
    data,
    rightsHolderId,
    publicSearchType,
    catalogType,
    historyType,
    draftType,
    claimType,
    claimedShare,
    handleDeleteDraft,
    selectWorkForMatching,
    selectWorkForClaiming,
    onCatalogClick,
}: WorkItemProps) => {
    const [extendedInfoIsOpen, setExtendedInfoIsOpen] = useState(false)

    const onToggleExtendedInfo = () => {
        setExtendedInfoIsOpen(!extendedInfoIsOpen)
    }

    const viewDetails = () => {
        if (navigate && rightsHolderId) {
            if (onCatalogClick) {
                onCatalogClick()
            }
            const path = getCatalogPath(data.isComplete, rightsHolderId, data.id as number)
            navigate(`${path}`)
        }
    }

    const {
        id,
        title,
        updateDate,
        rightsholderProprietaryId,
        iswc,
        isComplete, // isComplete === !typeDraft
        originalPublishers,
        writers,
        recordings,
        matchedRecordings,
        registrationHistoryStatus,
        registrationHistoryDate,
        registrationHistoryIndicator,
        alternativeTitles,
        songCode,
        holdTypeId,
        sourceId,
    } = data

    let totalKnownShares = data.totalKnownShares
    totalKnownShares = totalKnownShares ? Math.round(totalKnownShares * 100) / 100 : 0

    let allPublishersCount: number = originalPublishers.length
    originalPublishers.length &&
        originalPublishers.forEach((op: OriginalPublisherModel) => {
            allPublishersCount += op.administratorPublishers.length
        })

    const sourceName = getSourceNameBySourceId(sourceId)

    const catalogPath = rightsHolderId ? getCatalogPath(isComplete, rightsHolderId, id as number) : ''

    return (
        <div className="work-item-component">
            <div className={clsx('item-head', extendedInfoIsOpen && 'extended-info')}>
                <WorkItemTitleRow
                    title={title}
                    extendedInfoIsOpen={extendedInfoIsOpen}
                    onToggleExtendedInfo={onToggleExtendedInfo}
                    viewDetails={viewDetails}
                    publicSearchType={publicSearchType}
                    claimType={claimType}
                    historyType={historyType}
                    draftType={draftType}
                    songCode={songCode}
                    id={id}
                    catalogPath={catalogPath}
                    onCatalogClick={onCatalogClick}
                    handleDeleteDraft={handleDeleteDraft}
                    registrationHistoryStatus={registrationHistoryStatus}
                    registrationHistoryDate={registrationHistoryDate}
                    selectWorkForMatching={selectWorkForMatching}
                    selectWorkForClaiming={selectWorkForClaiming}
                />
                {isComplete && registrationHistoryIndicator && (
                    <div className="tags">
                        <TagComponent type={registrationHistoryIndicator} name={registrationHistoryIndicator} />
                    </div>
                )}
                <div className="head-sub-info">
                    <WorkItemSharesInfo
                        writers={writers}
                        claimedShare={claimedShare}
                        claimType={claimType}
                        totalKnownShares={totalKnownShares}
                        publicSearchType={publicSearchType}
                        catalogType={catalogType}
                        historyType={historyType}
                    />
                </div>
                <div className="head-sub-info sub-info-line-2">
                    <WorkItemDatesInfo
                        updateDate={updateDate}
                        historyType={historyType}
                        sourceName={sourceName}
                        rightsholderProprietaryId={rightsholderProprietaryId}
                    />
                </div>
            </div>
            {extendedInfoIsOpen ? (
                <ExtendInfoToWorkItem
                    writers={writers}
                    alternativeTitles={alternativeTitles}
                    originalPublishers={originalPublishers}
                    matchedRecordings={matchedRecordings}
                    holdTypeId={holdTypeId}
                    iswc={iswc}
                    songCode={songCode}
                />
            ) : (
                <div className="item-sub-info">
                    <div>
                        {songCode && (
                            <div className="small-2">
                                <span>MLC Song Code:</span> <span className="bold">{songCode}</span>
                            </div>
                        )}
                        {iswc && (
                            <div className="small-2">
                                <span>ISWC:</span> <span className="bold">{iswc}</span>
                            </div>
                        )}
                    </div>
                    <div>
                        <p className="small-2">Publishers ({allPublishersCount})</p>
                        {originalPublishers && originalPublishers.length ? (
                            <p className="small-2 bold">{originalPublishers[0].publisherName}</p>
                        ) : null}
                    </div>
                    <WorkItemRecordings
                        recordings={recordings}
                        matchedRecordings={matchedRecordings}
                        draftType={draftType}
                    />
                </div>
            )}
        </div>
    )
}

export default WorkItem
